<script>
  import { createEventDispatcher, getContext, onDestroy } from "svelte";
  import {
    createRandomEncoding,
    unpackNibblesLE,
    packNibblesLE,
    binaryStringToByteLE,
    byteToBinaryStringLE,
    StandardRules,
  } from "bitframes";
  import Button from "./Button.svelte";
  import Lock from "../icons/lock.svelte";
  import prng from "../util/global-prng.js";
  import LinkButton from "./LinkButton.svelte";
  import ColorPicker from "./ColorPicker.svelte";
  import { getBackground, getPalette } from "../util/constants";

  export let encoding;
  export let decoded;

  let curPalette = getPalette({ system: encoding[0] });
  $: curPalette = getPalette({ system: encoding[0] });

  const dispatch = createEventDispatcher();

  let _wasLocked = null;
  let isLocked = false;
  let target = 0;
  let lastDimIndexChanged = 0;

  $: {
    // const pat = binaryStringToByteLE(decoded.layers[target].pattern);
    // console.log(pat);
    encoding, isLocked, updateLock();
  }

  // visible: true, // 1 bit
  // colors: [0, 1], // 8 bits
  // dimensions: [dim, dim], // 8 bits
  // pattern: binaryStringToByteLE("10100010"), // 8 bits
  // rule: StandardRules.IDENTITY, // 8 bits
  // scale: 0, // 4 bits
  // skip: 0,
  // skipMode: SkipMode.EQUAL, // 2 bits
  // fillMode: FillMode.CENTER, // 2 bits
  // flipMode: FlipMode.HORIZONTAL, // 2 bits
  // wrap: true, // 1 bit

  function updateLock() {
    console.log("needs to change", isLocked);
    if (isLocked) {
      const lastDims = decoded.layers[target].dimensions.slice();
      if (lastDimIndexChanged == 0) {
        decoded.layers[target].dimensions[1] =
          decoded.layers[target].dimensions[0];
      } else {
        decoded.layers[target].dimensions[0] =
          decoded.layers[target].dimensions[1];
      }

      if (
        lastDims[0] !== decoded.layers[target].dimensions[0] ||
        lastDims[1] !== decoded.layers[target].dimensions[1]
      ) {
        changed();
      }
    }
  }

  function keydown(ev) {
    if (ev.ctrlKey || ev.metaKey) {
      const n = parseInt(ev.key, 10);
      if (n >= 1 && n <= 5) {
        target = n - 1;
      }
    }
  }
  function changed() {
    dispatch("applyEncoding", decoded);
  }

  function createEpmtyLayer() {
    return {
      visible: false, // 1 bit
      colors: [0, 0], // 8 bits
      dimensions: [0, 0], // 8 bits
      pattern: "00000000", // 8 bits
      rule: 0, // 8 bits
      scale: 0, // 4 bits
      skip: 0,
      skipMode: 0, // 2 bits
      fillMode: 0, // 2 bits
      flipMode: 0, // 2 bits
      wrap: false, // 1 bit
    };
  }
</script>

<div class="container">
  <div class="row">
    <Button
      text="Identity"
      fitHeight
      on:click={(ev) => {
        ev.preventDefault();
        for (let i = 0; i < decoded.layers.length; i++) {
          decoded.layers[i] = createEpmtyLayer();
        }
        isLocked = true;
        target = 0;
        decoded.layers[0].visible = true;
        decoded.layers[0].pattern = "10101010";
        decoded.layers[0].dimensions = [4, 4];
        decoded.layers[0].colors = [1, 0];
        decoded.layers[0].rule = StandardRules.COMPLEMENT;
        decoded.layers[0].scale = 1;
        changed();
      }}
    ></Button>
  </div>
  <div class="row targets">
    <div class="label">Layer:</div>
    {#each decoded.layers as _, i}
      <Button
        toggled={target === i}
        on:click={(ev) => {
          target = i;
        }}
        fitHeight
        square
        small
        text={String(i + 1)}
      ></Button>
    {/each}
  </div>
  <div class="row">
    <div class="label"></div>
    <Button
      text="Visible"
      toggled={decoded.layers[target].visible}
      small={true}
      radio
      fitHeight
      on:click={(ev) => {
        ev.preventDefault();
        decoded.layers[target].visible = !decoded.layers[target].visible;
        changed();
      }}
    ></Button>
    <Button
      text="Reset"
      small={true}
      fitHeight
      on:click={(ev) => {
        ev.preventDefault();
        decoded.layers[target] = createEpmtyLayer();
        changed();
      }}
    ></Button>
  </div>
  <div class="row">
    <div class="label">
      <LinkButton
        button
        on:click={(ev) => {
          ev.preventDefault();
          decoded.layers[target].colors = unpackNibblesLE(
            prng.rangeFloor(0, 256)
          );
          changed();
          console.log(decoded.layers[target].colors);
        }}>Color</LinkButton
      >
    </div>
    {#each [0, "swap", 1] as index}
      {#if index == "swap"}
        <button
          class="swap-color swatch-button"
          on:click={(ev) => {
            ev.preventDefault();
            decoded.layers[target].colors.reverse();
            changed();
          }}>↔</button
        >
      {:else}
        <ColorPicker
          id={decoded.layers[target].colors[index]}
          palette={curPalette}
          system={encoding[0]}
          on:select={(ev) => {
            const ret = ev.detail;
            if (ret) {
              decoded.layers[target].colors[index] = ret.index;
              changed();
            }
          }}
        ></ColorPicker>
      {/if}
    {/each}
  </div>
  <div class="row">
    <div class="label">
      <LinkButton
        button
        on:click={(ev) => {
          ev.preventDefault();
          decoded.layers[target].dimensions = unpackNibblesLE(
            prng.rangeFloor(0, 256)
          );
          changed();
        }}>Dimensions</LinkButton
      >
    </div>
    {#each [0, 1] as i}
      <input
        onwheel="return false;"
        type="number"
        min={0}
        max={15}
        step={1}
        value={decoded.layers[target].dimensions[i]}
        on:input={(ev) => {
          decoded.layers[target].dimensions[i] = Math.max(
            0,
            Math.min(15, parseInt(ev.target.value, 10) || 0)
          );
          lastDimIndexChanged = i;
          changed();
        }}
      />
    {/each}
    <Button
      text=""
      square
      small={true}
      fitHeight
      radio
      bind:toggled={isLocked}
      icon={Lock}
      hotkey="d"
      on:click={(ev) => {
        ev.preventDefault();
        // if (lastDimIndexChanged == 0) {
        //   decoded.layers[target].dimensions[1] =
        //     decoded.layers[target].dimensions[0];
        // } else {
        //   decoded.layers[target].dimensions[0] =
        //     decoded.layers[target].dimensions[1];
        // }
        // changed();
      }}
    ></Button>
  </div>
  <div class="row">
    <div class="label">
      <LinkButton
        button
        on:click={(ev) => {
          ev.preventDefault();
          decoded.layers[target].pattern = byteToBinaryStringLE(
            prng.rangeFloor(0, 256)
          );
          changed();
        }}>Pattern</LinkButton
      >
    </div>
    <div class="pattern-cell-container">
      {#each Array(8).fill(0) as _, i}
        <button
          class="pattern-cell"
          on:click={(ev) => {
            ev.preventDefault();
            const bits = decoded.layers[target].pattern.split("");
            bits[i] = bits[i] == "1" ? "0" : "1";
            decoded.layers[target].pattern = bits.join("");
            changed();
          }}
          class:active={decoded.layers[target].pattern.charAt(i) == "1"}
        >
          {decoded.layers[target].pattern.charAt(i)}
        </button>
      {/each}
    </div>
  </div>
  <div class="row">
    <div class="label">
      <LinkButton
        button
        on:click={(ev) => {
          ev.preventDefault();
          decoded.layers[target].rule = prng.rangeFloor(0, 256);
          changed();
        }}>Rule</LinkButton
      >
    </div>
    <input
      onwheel="return false;"
      type="number"
      min={0}
      max={0xff}
      step={1}
      value={decoded.layers[target].rule}
      on:input={(ev) => {
        decoded.layers[target].rule = Math.max(
          0,
          Math.min(0xff, parseInt(ev.target.value, 10) || 0)
        );
        changed();
      }}
    />
    {#each [["=", StandardRules.IDENTITY], ["!", StandardRules.COMPLEMENT], ["<<", StandardRules.LEFT_SHIFT], [">>", StandardRules.RIGHT_SHIFT]] as [id, n]}
      <Button
        text={id}
        square
        small={true}
        fitHeight
        on:click={(ev) => {
          ev.preventDefault();
          decoded.layers[target].rule = n;
          changed();
        }}
      ></Button>
    {/each}
  </div>

  <div class="row">
    <div class="label">
      <LinkButton
        button
        on:click={(ev) => {
          ev.preventDefault();
          decoded.layers[target].skip = prng.rangeFloor(0, 16);
          changed();
        }}>Skip</LinkButton
      >
    </div>
    <input
      onwheel="return false;"
      type="number"
      min={0}
      max={15}
      step={1}
      value={decoded.layers[target].skip}
      on:input={(ev) => {
        decoded.layers[target].skip = Math.max(
          0,
          Math.min(15, parseInt(ev.target.value, 10) || 0)
        );
        changed();
      }}
    />
    <Button
      fitHeight
      small
      square
      text="0"
      on:click={(ev) => {
        ev.preventDefault();
        decoded.layers[target].skip = 0;
        changed();
      }}
    />
  </div>

  <div class="row">
    <div class="label">
      <LinkButton
        button
        on:click={(ev) => {
          ev.preventDefault();
          decoded.layers[target].scale = prng.rangeFloor(0, 16);
          changed();
        }}>Scale</LinkButton
      >
    </div>
    <input
      onwheel="return false;"
      type="number"
      min={0}
      max={15}
      step={1}
      value={decoded.layers[target].scale}
      on:input={(ev) => {
        decoded.layers[target].scale = Math.max(
          0,
          Math.min(15, parseInt(ev.target.value, 10) || 0)
        );
        changed();
      }}
    />
    <Button
      fitHeight
      small
      square
      text="0"
      on:click={(ev) => {
        ev.preventDefault();
        decoded.layers[target].scale = 0;
        changed();
      }}
    />
  </div>

  {#each ["skipMode", "fillMode", "flipMode"] as label, i}
    <div class="row">
      <div class="label">
        <LinkButton
          button
          on:click={(ev) => {
            ev.preventDefault();
            decoded.layers[target][label] = prng.rangeFloor(0, 16);
            changed();
          }}>{label}</LinkButton
        >
      </div>
      {#each [0, 1, 2, 3] as idx}
        <Button
          fitHeight
          small
          square
          radio
          toggled={decoded.layers[target][label] === idx}
          text={String(idx)}
          on:click={(ev) => {
            ev.preventDefault();
            decoded.layers[target][label] = idx;
            changed();
          }}
        />
      {/each}
    </div>
  {/each}

  <div class="row">
    <Button
      fitHeight
      small
      square
      radio
      toggled={Boolean(decoded.layers[target].wrap)}
      text={"Wrap"}
      on:click={(ev) => {
        ev.preventDefault();
        decoded.layers[target].wrap = ev.detail.toggled;
        console.log(ev.detail);
        changed();
      }}
    />
  </div>
</div>

<svelte:window on:keydown|nonpassive={keydown} />

<style>
  .container {
    display: flex;
    gap: 20px;
    align-items: flex-start;
    width: 100%;
    flex-direction: column;
    --swatch-cell-size: 30px;
    --border-radius: 0px;
  }
  .pattern-cell-container {
    align-items: flex-start;
    flex-direction: row;
    display: flex;
    /* gap: 5px; */
  }
  .label {
    width: 90px;
    /* flex-grow: 1; */
  }
  .pattern-cell {
    width: 28px;
    height: 28px;
    border: 1px solid black;
    border-left: 0;
    align-items: center;
    justify-content: center;
    display: flex;
    appearance: none;
    cursor: pointer;
    background: none;
    border-radius: 0;
  }
  .pattern-cell:first-child {
    border-left: 1px solid black;
  }
  .pattern-cell.active {
    background-color: var(--accent-color);
  }
  .row {
    gap: 10px;
    align-items: center;
    display: flex;
    height: 30px;
    width: 100%;
    /* border-bottom: 1px dotted black; */
    padding: 0px 0px;
    /* padding-bottom: 5px; */
  }
  .targets {
    height: 30px;
  }
</style>
